/**
 * Screenplays: the text is Fountain (fountain.io), the plain-text screenplay standard that
 * Highland, Slugline and Final Draft read. This file reads it into elements, lays the elements
 * out as pages in the industry's measures (Courier 12, 10 characters an inch, 6 lines an inch,
 * 1.5 inch left margin, 1 inch elsewhere), lists the scenes, splices a rewritten scene or more
 * pages back into the text, and turns the elements back into Fountain for the editor, which
 * works on blocks (one element each) and writes them out again. Pure: nothing here touches
 * the store or the page.
 */

export type Paper = 'A4' | 'Letter';

export type ElementKind = 'scene_heading' | 'action' | 'character' | 'parenthetical' | 'dialogue' | 'transition' | 'centered' | 'page_break';

export interface Element {
    kind: ElementKind;
    text: string;
    /** The index of the scene this element is in (its heading first); -1 before the first heading */
    scene: number;
}

/** The title page's fields, as Fountain names them: Title, Credit, Author, Source, Draft date, Contact, ... */
export type TitlePage = Record<string, string>;

export interface Screenplay {
    titlePage: TitlePage;
    elements: Element[];
    /** Where the body begins in the source, in lines (after the title page) */
    bodyStart: number;
}

export interface Line {
    kind: ElementKind | 'more' | 'blank';
    text: string;
    /** Characters in from the left margin */
    indent: number;
    scene: number;
    /** The element this line is of (its index in the screenplay), and which of its wrapped lines; -1 for blank lines and the lines pagination adds */
    element: number;
    row: number;
}

export interface Page {
    number: number;
    lines: Line[];
}

export interface Scene {
    index: number;
    heading: string;
    /** The page it starts on (1-based) */
    page: number;
    /** How many lines it takes, pagination's blank lines included */
    lines: number;
    /** Lines of the Fountain source it spans: [from, to) */
    from: number;
    to: number;
}

/** The measures of a page, in Courier characters and lines */
export interface PaperMeasures {
    /** Lines of body text a page holds */
    linesPerPage: number;
    /** Characters across the body, from the left margin to the right */
    width: number;
    widthPt: number;
    heightPt: number;
}

/** 55 lines for Letter (9 inches of body at 6 lines an inch), 58 for A4; 60 characters across for Letter, 57 for A4 */
export const PAPER: Record<Paper, PaperMeasures> = {
    Letter: { linesPerPage: 55, width: 60, widthPt: 612, heightPt: 792 },
    A4: { linesPerPage: 58, width: 57, widthPt: 595.28, heightPt: 841.89 },
};

/** The page's margins and type, in points: Courier 12 (10 characters an inch, 6 lines an inch), 1.5 inch at the left, 1 inch elsewhere */
export const MARGIN_LEFT_PT = 108;
export const MARGIN_PT = 72;
export const LINE_PT = 12;
export const CHAR_PT = 7.2;

/** Where each kind of line starts (characters from the left margin) and how wide it may be */
const DIALOGUE_INDENT = 10;
const DIALOGUE_WIDTH = 35;
const PARENTHETICAL_INDENT = 16;
const PARENTHETICAL_WIDTH = 25;
const CHARACTER_INDENT = 22;

/** Where a kind of element sits on the page: characters in from the left margin, and how many across it may use */
export const measuresOf = (kind: ElementKind, paper: Paper): { indent: number; width: number; align: 'left' | 'right' | 'center' } => {
    const w = PAPER[paper].width;
    switch (kind) {
        case 'character': return { indent: CHARACTER_INDENT, width: w - CHARACTER_INDENT, align: 'left' };
        case 'parenthetical': return { indent: PARENTHETICAL_INDENT, width: PARENTHETICAL_WIDTH, align: 'left' };
        case 'dialogue': return { indent: DIALOGUE_INDENT, width: DIALOGUE_WIDTH, align: 'left' };
        case 'transition': return { indent: 0, width: w, align: 'right' };
        case 'centered': return { indent: 0, width: w, align: 'center' };
        default: return { indent: 0, width: w, align: 'left' };
    }
};

const TITLE_KEYS = ['title', 'credit', 'author', 'authors', 'source', 'draft date', 'date', 'contact', 'copyright', 'notes', 'revision'];
const SCENE_HEADING = /^(INT|EXT|EST|INT\.?\/EXT|I\/E)[ .]/i;
const TRANSITION_WORDS = /^(FADE IN:|FADE OUT\.|FADE TO BLACK\.|CUT TO BLACK\.|THE END\.?|END\.?|SMASH CUT:|MATCH CUT:|DISSOLVE:)$/;
const CHARACTER = /^[A-Z0-9][A-Z0-9 .,'\-#&]*(\s*\([^)]*\))*\s*\^?$/;

const isBlank = (line: string) => line.trim() === '';
const isCaps = (line: string) => line === line.toUpperCase() && /[A-Z]/.test(line);

/** The source without boneyards (/* *\/) and notes ([[ ]]), lines kept where they were */
const stripped = (text: string): string[] => {
    const noBoneyard = text.replace(/\/\*[\s\S]*?\*\//g, (match) => match.replace(/[^\n]/g, ''));
    const noNotes = noBoneyard.replace(/\[\[[\s\S]*?\]\]/g, (match) => match.replace(/[^\n]/g, ''));
    return noNotes.replace(/\r\n?/g, '\n').split('\n');
};

/** The title page at the top of the source: "Key: value" lines until the first blank line, the first key one of the title page's own (FADE IN: is not) */
export const parseTitlePage = (lines: string[]): { titlePage: TitlePage; bodyStart: number } => {
    const titlePage: TitlePage = {};
    const first = lines.length ? /^([A-Za-z ]+):/.exec(lines[0]) : null;
    if (!first || !TITLE_KEYS.includes(first[1].trim().toLowerCase())) return { titlePage, bodyStart: 0 };
    let i = 0;
    let key: string | null = null;
    for (; i < lines.length; i++) {
        const line = lines[i];
        if (isBlank(line)) { i++; break; }
        const match = /^([A-Za-z ]+):\s*(.*)$/.exec(line);
        if (match && TITLE_KEYS.includes(match[1].trim().toLowerCase())) {
            key = match[1].trim().toLowerCase();
            titlePage[key] = match[2].trim();
        } else if (key && /^\s/.test(line)) {
            titlePage[key] = `${titlePage[key]}${titlePage[key] ? '\n' : ''}${line.trim()}`;
        } else if (match) {
            key = match[1].trim().toLowerCase();
            titlePage[key] = match[2].trim();
        } else {
            // Not a title page after all
            return { titlePage: {}, bodyStart: 0 };
        }
    }
    return { titlePage, bodyStart: i };
};

/** The Fountain source as elements */
export const parseFountain = (text: string): Screenplay => {
    const lines = stripped(text);
    const { titlePage, bodyStart } = parseTitlePage(lines);
    const elements: Element[] = [];
    let scene = -1;
    let i = bodyStart;
    const push = (kind: ElementKind, value: string) => {
        if (kind === 'scene_heading') scene++;
        elements.push({ kind, text: value, scene });
    };
    /** The run of lines until a blank one, their breaks kept: one action */
    const readAction = (first: string) => {
        const run: string[] = [first];
        i++;
        while (i < lines.length && !isBlank(lines[i])) { run.push(lines[i]); i++; }
        push('action', run.join('\n').trimEnd());
    };
    while (i < lines.length) {
        const raw = lines[i];
        const line = raw.trim();
        const prevBlank = i === 0 || i === bodyStart || isBlank(lines[i - 1]);
        const nextBlank = i + 1 >= lines.length || isBlank(lines[i + 1]);
        if (isBlank(raw)) { i++; continue; }
        if (/^={3,}\s*$/.test(line)) { push('page_break', ''); i++; continue; }
        // Forced kinds
        if (line.startsWith('!')) { readAction(raw.replace(/^\s*!/, '')); continue; }
        if (line.startsWith('.') && !line.startsWith('..')) { push('scene_heading', line.slice(1).trim().replace(/\s*#[^#]*#\s*$/, '').toUpperCase()); i++; continue; }
        if (line.startsWith('>') && line.endsWith('<')) { push('centered', line.slice(1, -1).trim()); i++; continue; }
        if (line.startsWith('>')) { push('transition', line.slice(1).trim().toUpperCase()); i++; continue; }
        if (line.startsWith('@')) { i = readDialogue(lines, i, line.slice(1).trim(), push); continue; }
        if (line.startsWith('~')) { push('action', line.slice(1)); i++; continue; }
        if (prevBlank && SCENE_HEADING.test(line)) { push('scene_heading', line.replace(/\s*#[^#]*#\s*$/, '').toUpperCase()); i++; continue; }
        if (prevBlank && nextBlank && isCaps(line) && (line.endsWith('TO:') || TRANSITION_WORDS.test(line))) { push('transition', line); i++; continue; }
        if (prevBlank && !nextBlank && isCaps(line) && CHARACTER.test(line) && line.length <= 50) { i = readDialogue(lines, i, line.replace(/\s*\^$/, ''), push); continue; }
        readAction(raw);
    }
    return { titlePage, elements, bodyStart };
};

/** A character cue and what follows it until a blank line: parentheticals and dialogue */
const readDialogue = (lines: string[], at: number, name: string, push: (kind: ElementKind, text: string) => void): number => {
    push('character', name.toUpperCase());
    let i = at + 1;
    let said: string[] = [];
    const flush = () => { if (said.length) { push('dialogue', said.join(' ')); said = []; } };
    while (i < lines.length && !isBlank(lines[i])) {
        const line = lines[i].trim();
        if (line.startsWith('(') && line.endsWith(')')) { flush(); push('parenthetical', line); }
        else said.push(line);
        i++;
    }
    flush();
    return i;
};

// ── Wrapping ─────────────────────────────────────────────────────────────────

/** One line of wrapped text and where it lies in the text: [start, end) */
export interface WrappedLine {
    text: string;
    start: number;
    end: number;
}

/** Where the words of a line are: [start, end) offsets */
const wordsOf = (line: string): [number, number][] => {
    const out: [number, number][] = [];
    const re = /\S+/g;
    let match: RegExpExecArray | null;
    while ((match = re.exec(line))) out.push([match.index, match.index + match[0].length]);
    return out;
};

/**
 * The text wrapped to a width, as the lines are shown and where each lies in the text. A line
 * ends at the last word that fits; the spaces after it are taken by the break. A word longer
 * than the width is broken at the width. A line break in the text starts a new line, and the
 * last line of each keeps the spaces after its last word (what is being typed shows as typed).
 */
export const wrapLines = (text: string, width: number): WrappedLine[] => {
    const out: WrappedLine[] = [];
    let base = 0;
    for (const hard of text.split('\n')) {
        const words = wordsOf(hard);
        let lineStart = 0;
        let lineEnd = 0;
        let any = false;
        for (const [wordStart, wordEnd] of words) {
            if (any && wordEnd - lineStart > width) {
                out.push({ text: hard.slice(lineStart, lineEnd), start: base + lineStart, end: base + lineEnd });
                lineStart = wordStart;
                any = false;
            }
            while (wordEnd - lineStart > width) {
                const cut = lineStart + width;
                out.push({ text: hard.slice(lineStart, cut), start: base + lineStart, end: base + cut });
                lineStart = cut;
            }
            lineEnd = wordEnd;
            any = true;
        }
        out.push({ text: hard.slice(lineStart), start: base + lineStart, end: base + hard.length });
        base += hard.length + 1;
    }
    return out;
};

/** Words wrapped to a width, as printed (no spaces left at the ends of lines) */
export const wrap = (text: string, width: number): string[] => wrapLines(text, width).map(line => line.text.trimEnd());

/** The lines an element takes, before pagination */
const linesOf = (element: Element, index: number, paper: Paper): Line[] => {
    if (element.kind === 'page_break') return [];
    const { indent, width, align } = measuresOf(element.kind, paper);
    return wrap(element.text, width).map((text, row): Line => ({
        kind: element.kind,
        text,
        indent: align === 'right' ? Math.max(0, width - text.length) : align === 'center' ? Math.max(0, Math.floor((width - text.length) / 2)) : indent,
        scene: element.scene,
        element: index,
        row,
    }));
};

const blank = (scene: number): Line => ({ kind: 'blank', text: '', indent: 0, scene, element: -1, row: -1 });

export interface PaginateOptions {
    /** Dialogue too long for the foot of a page breaks there with (MORE) and (CONT'D); off, a speech moves whole to the next page */
    splitDialogue?: boolean;
}

/**
 * The elements as pages: a blank line between elements (two before a scene heading), a scene
 * heading never left alone at the foot of a page, a character never parted from the first two
 * lines they say, and dialogue that breaks across pages told with (MORE) and (CONT'D).
 */
export const paginate = (screenplay: Screenplay, paper: Paper = 'A4', options: PaginateOptions = {}): Page[] => {
    const splitDialogue = options.splitDialogue ?? true;
    const measures = PAPER[paper];
    const N = measures.linesPerPage;
    const pages: Page[] = [];
    let current: Line[] = [];
    const newPage = () => { pages.push({ number: pages.length + 1, lines: current }); current = []; };
    const room = () => N - current.length;
    const { elements } = screenplay;

    for (let e = 0; e < elements.length; e++) {
        const element = elements[e];
        if (element.kind === 'page_break') { if (current.length) newPage(); continue; }
        let lines = linesOf(element, e, paper);
        if (lines.length === 0) continue;
        // The space before it: none at the top of a page; two blank lines before a scene heading, one otherwise
        const gap = current.length === 0 ? 0 : element.kind === 'scene_heading' ? 2 : element.kind === 'parenthetical' || element.kind === 'dialogue' ? 0 : 1;
        const next = elements[e + 1];
        // What must stay with it: a heading keeps two lines of what follows; a character keeps two lines said
        let keepWith = 0;
        if (element.kind === 'scene_heading' && next) keepWith = Math.min(2, linesOf(next, e + 1, paper).length) + 1;
        if (element.kind === 'character' && next && (next.kind === 'dialogue' || next.kind === 'parenthetical')) keepWith = Math.min(2, linesOf(next, e + 1, paper).length);
        const canSplit = splitDialogue && element.kind === 'dialogue' && lines.length > 2;
        if (gap + lines.length + keepWith > room() && current.length > 0 && !canSplit) {
            // It does not fit whole: a new page, unless it is dialogue long enough to break
            newPage();
        }
        if (current.length > 0) for (let g = 0; g < gap; g++) current.push(blank(element.scene));
        // Dialogue that is too long for the page breaks with (MORE), and the name is said again with (CONT'D)
        while (canSplit && lines.length > room()) {
            const fits = room() - 1; // a line for (MORE)
            if (fits < 2) { newPage(); break; }
            current.push(...lines.slice(0, fits));
            current.push({ kind: 'more', text: '(MORE)', indent: PARENTHETICAL_INDENT, scene: element.scene, element: -1, row: -1 });
            lines = lines.slice(fits);
            newPage();
            const who = [...elements.slice(0, e).reverse()].find(el => el.kind === 'character');
            if (who) current.push({ kind: 'character', text: `${who.text.replace(/\s*\(CONT'D\)$/, '')} (CONT'D)`, indent: CHARACTER_INDENT, scene: element.scene, element: -1, row: -1 });
        }
        if (lines.length > room() && current.length > 0) newPage();
        current.push(...lines.slice(0, N));
        lines = lines.slice(N);
        while (lines.length > 0) { newPage(); current.push(...lines.slice(0, N)); lines = lines.slice(N); }
    }
    if (current.length > 0 || pages.length === 0) newPage();
    return pages;
};

/** A run of an element's lines on one page: where it starts (the page, the line on it) and which of the element's wrapped lines it holds */
export interface Part {
    /** The page's index (0-based) */
    page: number;
    /** The line of the page it starts on */
    top: number;
    /** The element's first wrapped line in this part */
    from: number;
    count: number;
}

/** Where each element's lines lie on the pages, by the element's index: one part, or more when a speech breaks across pages */
export const layoutOf = (pages: ReadonlyArray<Page>): Map<number, Part[]> => {
    const parts = new Map<number, Part[]>();
    pages.forEach((page, p) => page.lines.forEach((line, i) => {
        if (line.element < 0) return;
        const list = parts.get(line.element) ?? [];
        const last = list[list.length - 1];
        if (last && last.page === p && last.top + last.count === i && last.from + last.count === line.row) last.count++;
        else list.push({ page: p, top: i, from: line.row, count: 1 });
        parts.set(line.element, list);
    }));
    return parts;
};

/** The scenes, with where each starts in the pages and in the source */
export const scenesOf = (text: string, pages: Page[]): Scene[] => {
    const lines = stripped(text);
    const { bodyStart } = parseTitlePage(lines);
    const scenes: Scene[] = [];
    // Where each heading is in the source
    for (let i = bodyStart; i < lines.length; i++) {
        const line = lines[i].trim();
        const prevBlank = i === bodyStart || isBlank(lines[i - 1]);
        const forced = line.startsWith('.') && !line.startsWith('..');
        if (forced || (prevBlank && SCENE_HEADING.test(line))) {
            if (scenes.length) scenes[scenes.length - 1].to = i;
            scenes.push({ index: scenes.length, heading: (forced ? line.slice(1).trim() : line).replace(/\s*#[^#]*#\s*$/, '').toUpperCase(), page: 1, lines: 0, from: i, to: lines.length });
        }
    }
    for (const page of pages) {
        for (const line of page.lines) {
            const scene = scenes[line.scene];
            if (!scene) continue;
            if (scene.lines === 0) scene.page = page.number;
            scene.lines++;
        }
    }
    return scenes;
};

/** Minutes a screenplay runs, by the rule of a page a minute */
export const minutesOf = (pages: number): string => (pages <= 1 ? 'about a minute' : `about ${pages} min`);

/** A screenplay with one scene replaced by new Fountain (the title page and the other scenes untouched) */
export const replaceScene = (text: string, scene: Scene, replacement: string): string => {
    const lines = text.replace(/\r\n?/g, '\n').split('\n');
    const before = lines.slice(0, scene.from);
    const after = lines.slice(scene.to);
    const body = replacement.replace(/\r\n?/g, '\n').trim();
    const tail = after.length && !isBlank(after[0]) ? ['', ...after] : after;
    return [...before, body, ...tail].join('\n').replace(/\n{3,}/g, '\n\n');
};

/** More pages at the end */
export const appendText = (text: string, more: string): string => `${text.replace(/\s+$/, '')}\n\n${more.replace(/\r\n?/g, '\n').trim()}\n`;

/** A model's answer without the code fences it may have put around it */
const unfenced = (answer: string): string => {
    let text = answer.replace(/\r\n?/g, '\n').trim();
    const fence = /^```[a-z]*\n([\s\S]*?)\n```\s*$/i.exec(text);
    if (fence) text = fence[1].trim();
    return text.replace(/^```[a-z]*\n?/i, '').replace(/\n?```\s*$/, '').trim();
};

// ── Asking for a change, scene by scene ──────────────────────────────────────
// The model is given the script with a note before each scene, and answers with the scenes it
// changes alone, each under its note: the rest of the script is never written back, so a change
// of one line costs the tokens of one scene, and no other line can drift.

const OPENING_NOTE = '[[Opening]]';
const sceneNote = (index: number) => `[[Scene ${index + 1}]]`;
const ANSWER_NOTE = /^[ \t]*\[\[[ \t]*(?:Scene[ \t]+(\d+)|(Opening))[ \t]*\]\][ \t]*$/gim;
const isHeadingLine = (line: string) => (line.startsWith('.') && !line.startsWith('..')) || SCENE_HEADING.test(line);

/**
 * The body of a screenplay with a note before each scene ("[[Scene 3]]", and "[[Opening]]"
 * before whatever comes before the first heading), for the model to answer scene by scene.
 * Notes are not script (the parser drops them), so nothing the model echoes can end up on the page.
 */
export const numberedBody = (text: string): string => {
    const source = text.replace(/\r\n?/g, '\n');
    const lines = source.split('\n');
    const { bodyStart } = parseTitlePage(stripped(source));
    const scenes = scenesOf(source, []);
    const out: string[] = [];
    const opening = lines.slice(bodyStart, scenes[0]?.from ?? lines.length).join('\n').trim();
    if (opening) out.push(OPENING_NOTE, opening, '');
    scenes.forEach((scene, i) => out.push(sceneNote(i), lines.slice(scene.from, scene.to).join('\n').trim(), ''));
    return out.join('\n').trim();
};

/** Lines [from, to) of a script replaced by new text, a blank line kept on either side */
const replaceLines = (lines: string[], from: number, to: number, body: string): string[] => {
    const before = lines.slice(0, from);
    const after = lines.slice(to);
    const tail = body && after.length && !isBlank(after[0]) ? ['', ...after] : after;
    return [...before, ...(body ? [body] : []), ...tail];
};

/**
 * The model's answer to an ask, put into the script. Scenes answered under their notes replace
 * those scenes and nothing else (a new scene inside a chunk comes with it, after that scene);
 * an answer that is one scene beginning with a heading the script has once replaces that scene;
 * anything else is taken as the whole script, the title page kept if the answer left it out.
 */
export const applyAnswer = (text: string, answer: string): string => {
    const source = text.replace(/\r\n?/g, '\n');
    const scenes = scenesOf(source, []);
    const clean = unfenced(answer);
    const notes = Array.from(clean.matchAll(ANSWER_NOTE));
    if (notes.length) {
        const lines = source.split('\n');
        const edits = new Map<number, { to: number; body: string }>();
        notes.forEach((note, i) => {
            const start = (note.index ?? 0) + note[0].length;
            const end = i + 1 < notes.length ? (notes[i + 1].index ?? clean.length) : clean.length;
            const body = clean.slice(start, end).trim();
            if (note[2]) {
                edits.set(parseTitlePage(stripped(source)).bodyStart, { to: scenes[0]?.from ?? lines.length, body });
                return;
            }
            const scene = scenes[Number(note[1]) - 1];
            if (!scene) return;
            // The heading kept when the model left it out of its answer
            const first = body.split('\n').find(line => line.trim())?.trim() ?? '';
            edits.set(scene.from, { to: scene.to, body: !body || isHeadingLine(first) ? body : `${lines[scene.from]}\n\n${body}` });
        });
        if (edits.size) {
            // The later edits first, so the earlier lines keep their places
            let next = lines;
            for (const [from, edit] of [...edits.entries()].sort((a, b) => b[0] - a[0])) next = replaceLines(next, from, edit.to, edit.body);
            return next.join('\n').replace(/\n{3,}/g, '\n\n').trim();
        }
    }
    const whole = fountainFromAnswer(answer);
    const first = whole.split('\n').find(line => line.trim())?.trim() ?? '';
    const headings = whole.split('\n').filter(line => isHeadingLine(line.trim())).length;
    if (isHeadingLine(first) && headings === 1 && scenes.length > 1) {
        const heading = (first.startsWith('.') ? first.slice(1) : first).replace(/\s*#[^#]*#\s*$/, '').trim().toUpperCase();
        const matches = scenes.filter(scene => scene.heading === heading);
        if (matches.length === 1) return replaceScene(source, matches[0], whole);
    }
    const titlePage = parseFountain(source).titlePage;
    return titlePage.title && !parseFountain(whole).titlePage.title ? withTitlePage(whole, { title: titlePage.title, author: titlePage.author }) : whole;
};

/** A model's answer as Fountain: code fences and any words before the first line of script dropped */
export const fountainFromAnswer = (answer: string): string => {
    let text = unfenced(answer);
    // Anything said before the title page or the first scene heading is not script
    const match = /(?:^|\n|[.!?:]\s+)(Title:|FADE IN|\.?(?:INT|EXT|EST|INT\.?\/EXT|I\/E)[ .])/i.exec(text);
    if (match && match.index > 0) text = text.slice(match.index + match[0].length - match[1].length);
    return text.trim();
};

/** The title page as Fountain, from its fields */
export const titlePageText = (fields: { title?: string; author?: string; draftDate?: string; credit?: string }): string => {
    const lines: string[] = [];
    if (fields.title) lines.push(`Title: ${fields.title}`);
    if (fields.author) { lines.push(`Credit: ${fields.credit || 'Written by'}`); lines.push(`Author: ${fields.author}`); }
    if (fields.draftDate) lines.push(`Draft date: ${fields.draftDate}`);
    return lines.length ? `${lines.join('\n')}\n\n` : '';
};

/** The text with its title page set to these fields, the body kept */
export const withTitlePage = (text: string, fields: { title?: string; author?: string; draftDate?: string }): string => {
    const lines = text.replace(/\r\n?/g, '\n').split('\n');
    const { titlePage, bodyStart } = parseTitlePage(lines);
    const merged = { title: fields.title ?? titlePage.title, author: fields.author ?? titlePage.author, draftDate: fields.draftDate ?? titlePage['draft date'], credit: titlePage.credit };
    return `${titlePageText(merged)}${lines.slice(bodyStart).join('\n').replace(/^\n+/, '')}`;
};

// ── Blocks: what the editor works on ─────────────────────────────────────────

/** One element of the screenplay as the editor holds it */
export interface Block {
    id: string;
    kind: ElementKind;
    text: string;
}

/** The kinds a line can be, in the order Tab goes through them */
export const KINDS: ElementKind[] = ['scene_heading', 'action', 'character', 'parenthetical', 'dialogue', 'transition', 'centered'];

export const KIND_LABELS: Record<ElementKind, string> = {
    scene_heading: 'Scene heading', action: 'Action', character: 'Character', parenthetical: 'Parenthetical', dialogue: 'Dialogue', transition: 'Transition', centered: 'Centered', page_break: 'Page break',
};

/** Kinds whose words are set in capitals */
export const CAPS_KINDS: ReadonlySet<ElementKind> = new Set<ElementKind>(['scene_heading', 'character', 'transition']);

/** What usually follows a line of this kind, when Enter is pressed at its end */
export const kindAfter = (kind: ElementKind): ElementKind => ({
    scene_heading: 'action', action: 'action', character: 'dialogue', parenthetical: 'dialogue', dialogue: 'character', transition: 'scene_heading', centered: 'action', page_break: 'scene_heading',
} as const)[kind];

/** The next kind along (Tab), or the one before (Shift+Tab) */
export const kindBeside = (kind: ElementKind, back: boolean = false): ElementKind => {
    const at = KINDS.indexOf(kind);
    if (at < 0) return 'action';
    return KINDS[(at + (back ? KINDS.length - 1 : 1)) % KINDS.length];
};

/** What a line has become by what was typed in it: INT. or EXT. makes a scene heading, a parenthesis opens a parenthetical, CUT TO: is a transition */
export const kindTyped = (kind: ElementKind, text: string): ElementKind => {
    const line = text.trim();
    if (kind === 'action' && SCENE_HEADING.test(line)) return 'scene_heading';
    if (kind === 'dialogue' && line.startsWith('(')) return 'parenthetical';
    if (kind === 'action' && isCaps(line) && /^[A-Z][A-Z .'-]*TO:$/.test(line)) return 'transition';
    return kind;
};

let blockCount = 0;
export const blockId = (): string => `b${Date.now().toString(36)}${(blockCount++).toString(36)}${Math.random().toString(36).slice(2, 6)}`;

/** The screenplay's elements as blocks for the editor */
export const blocksOf = (screenplay: Screenplay): Block[] => screenplay.elements.map(element => ({ id: blockId(), kind: element.kind, text: element.text }));

/** The elements the blocks are, their scenes counted; a block with nothing in it still takes a line, so the editor has somewhere to put the caret */
export const elementsOf = (blocks: ReadonlyArray<Pick<Block, 'kind' | 'text'>>): Element[] => {
    let scene = -1;
    return blocks.map(block => {
        if (block.kind === 'scene_heading') scene++;
        return { kind: block.kind, text: block.text, scene };
    });
};

const TITLE_ORDER = ['title', 'credit', 'author', 'authors', 'source', 'draft date', 'date', 'contact', 'copyright', 'notes', 'revision'];

/** The title page as Fountain, every field kept */
export const titlePageFountain = (titlePage: TitlePage): string => {
    const keys = [...TITLE_ORDER.filter(key => titlePage[key]), ...Object.keys(titlePage).filter(key => !TITLE_ORDER.includes(key) && titlePage[key])];
    if (keys.length === 0) return '';
    const lines = keys.map(key => {
        const name = key.charAt(0).toUpperCase() + key.slice(1);
        const [first, ...rest] = titlePage[key].split('\n');
        return [`${name}: ${first}`, ...rest.map(line => `   ${line}`)].join('\n');
    });
    return `${lines.join('\n')}\n\n`;
};

/** Whether a line of speech or a parenthetical follows a character cue without a blank line */
const speech = (kind: ElementKind | undefined) => kind === 'dialogue' || kind === 'parenthetical';

/**
 * The elements as Fountain that reads back as the same elements: a heading, a cue or a
 * transition that Fountain would not see on its own is forced (. @ >), an action that reads
 * as something else is forced (!), and a cue's lines of speech follow it without a blank line.
 */
export const elementsToFountain = (elements: ReadonlyArray<Pick<Element, 'kind' | 'text'>>): string => {
    const out: string[] = [];
    let previous: ElementKind | undefined;
    for (let e = 0; e < elements.length; e++) {
        const element = elements[e];
        const next = elements[e + 1]?.kind;
        const text = element.text.replace(/\r\n?/g, '\n').split('\n').map(line => line.trimEnd()).join('\n').replace(/^\n+|\n+$/g, '');
        if (!text && element.kind !== 'page_break') { continue; }
        let written: string;
        switch (element.kind) {
            case 'page_break': written = '==='; break;
            case 'scene_heading': { const t = text.toUpperCase().trim(); written = SCENE_HEADING.test(t) ? t : `.${t}`; break; }
            case 'character': { const t = text.toUpperCase().trim(); written = CHARACTER.test(t) && !SCENE_HEADING.test(t) && t.length <= 50 && speech(next) ? t : `@${t}`; break; }
            case 'parenthetical': { const t = text.trim().replace(/^\(|\)$/g, '').trim(); written = `(${t})`; break; }
            case 'dialogue': written = text.replace(/\n+/g, ' ').trim(); break;
            case 'transition': { const t = text.toUpperCase().trim(); written = t.endsWith('TO:') || TRANSITION_WORDS.test(t) ? t : `> ${t}`; break; }
            case 'centered': written = `> ${text.replace(/\n+/g, ' ').trim()} <`; break;
            default: {
                const first = text.split('\n')[0].trim();
                const alone = !text.includes('\n');
                const forced = /^[.!>@~]/.test(first) && !first.startsWith('..')
                    || /^={3,}\s*$/.test(first)
                    || SCENE_HEADING.test(first)
                    || (isCaps(first) && (alone ? (first.endsWith('TO:') || TRANSITION_WORDS.test(first)) : (CHARACTER.test(first) && first.length <= 50)))
                    || (alone && isCaps(first) && CHARACTER.test(first) && first.length <= 50 && speech(next));
                written = forced ? `!${text}` : text;
            }
        }
        const together = (element.kind === 'parenthetical' || element.kind === 'dialogue') && (previous === 'character' || speech(previous));
        if (out.length) out.push(together ? '\n' : '\n\n');
        out.push(written);
        previous = element.kind;
    }
    return out.join('');
};

/** The whole screenplay as Fountain: the title page, then the blocks */
export const fountainOf = (titlePage: TitlePage, blocks: ReadonlyArray<Pick<Block, 'kind' | 'text'>>): string =>
    `${titlePageFountain(titlePage)}${elementsToFountain(blocks)}`;

// ── Versions ─────────────────────────────────────────────────────────────────

export interface ScreenplayVersion {
    at: number;
    /** Who made it: a person's id, or 'ai' */
    by: string;
    byName: string;
    /** What it is: "Draft 1 · written from the brief", "you edited scene 2", ... */
    note: string;
    text: string;
    pages: number;
}

/** One asking in the screenplay's chat: what was asked, what came of it */
export interface ChatTurn {
    at: number;
    ask: string;
    /** What the model did, in a line: "Scene 2 changed · 6 lines", or what went wrong */
    note: string;
    model: string;
    usd: number | null;
    /** Waiting to be kept or undone, kept, undone, written straight in (a first draft), or failed */
    status: 'pending' | 'kept' | 'undone' | 'written' | 'failed';
}

export interface ScreenplayMeta {
    title: string;
    author: string;
    paper: Paper;
    draft: number;
    versions: ScreenplayVersion[];
    /** The chat with the model, oldest first; empty for a card made before it had one */
    chat?: ChatTurn[];
}

/** So many turns of the chat are kept on the card */
export const MAX_CHAT = 40;

/** A turn added, or the one at the same moment replaced; the oldest let go past the limit */
export const addTurn = (chat: ReadonlyArray<ChatTurn> | undefined, turn: ChatTurn): ChatTurn[] => {
    const list = [...(chat ?? [])];
    const at = list.findIndex(t => t.at === turn.at);
    if (at >= 0) list[at] = turn; else list.push(turn);
    return list.slice(-MAX_CHAT);
};

// ── What the model changed ───────────────────────────────────────────────────

export interface WordPiece { text: string; op: 'same' | 'add' | 'del' }

/** One row of a script set against another: a line kept, added, taken out, or changed (with the words that changed) */
export interface DiffRow {
    op: 'same' | 'add' | 'del' | 'change';
    /** The line as it is after (for a line taken out, as it was) */
    element: Element;
    before?: Element;
    pieces?: WordPiece[];
}

/** The longest common run of two lists, as pairs of indices into each */
const lcs = <T,>(a: T[], b: T[]): [number, number][] => {
    const n = a.length;
    const m = b.length;
    const out: [number, number][] = [];
    if (n === 0 || m === 0) return out;
    if (n * m > 6_000_000) {
        // Too long to set every line against every other: lines are matched in step
        for (let i = 0; i < Math.min(n, m); i++) if (a[i] === b[i]) out.push([i, i]);
        return out;
    }
    const W = m + 1;
    const dp = new Uint32Array((n + 1) * W);
    for (let i = n - 1; i >= 0; i--) for (let j = m - 1; j >= 0; j--) dp[i * W + j] = a[i] === b[j] ? dp[(i + 1) * W + j + 1] + 1 : Math.max(dp[(i + 1) * W + j], dp[i * W + j + 1]);
    let i = 0;
    let j = 0;
    while (i < n && j < m) {
        if (a[i] === b[j]) { out.push([i, j]); i++; j++; }
        else if (dp[(i + 1) * W + j] >= dp[i * W + j + 1]) i++;
        else j++;
    }
    return out;
};

/** The words of one line set against another: what stayed, what came in, what went */
export const diffWords = (before: string, after: string): WordPiece[] => {
    // Each word with the spaces after it, matched by the word alone: a word at the end of a line is
    // the same word mid-line, and the spaces never pair words that have nothing in common
    const a = before.match(/\S+\s*|\s+/g) ?? [];
    const b = after.match(/\S+\s*|\s+/g) ?? [];
    const pairs = lcs(a.map(token => token.trim()), b.map(token => token.trim()));
    const pieces: WordPiece[] = [];
    const push = (text: string, op: WordPiece['op']) => {
        if (!text) return;
        const last = pieces[pieces.length - 1];
        if (last && last.op === op) last.text += text; else pieces.push({ text, op });
    };
    let i = 0;
    let j = 0;
    for (const [bi, ai] of pairs) {
        while (i < bi) push(a[i++], 'del');
        while (j < ai) push(b[j++], 'add');
        push(b[ai], 'same');
        i = bi + 1;
        j = ai + 1;
    }
    while (i < a.length) push(a[i++], 'del');
    while (j < b.length) push(b[j++], 'add');
    return pieces;
};

const keyOf = (element: Element) => `${element.kind}\n${element.text}`;

/**
 * A script set against the script the model made of it: every line kept, added or taken out,
 * and a line taken out beside a line added of the same kind as one line changed, its words
 * told apart.
 */
export const diffElements = (before: ReadonlyArray<Element>, after: ReadonlyArray<Element>): DiffRow[] => {
    const pairs = lcs(before.map(keyOf), after.map(keyOf));
    const rows: DiffRow[] = [];
    const flush = (dels: Element[], adds: Element[]) => {
        let d = 0;
        let a = 0;
        while (d < dels.length || a < adds.length) {
            if (d < dels.length && a < adds.length && dels[d].kind === adds[a].kind) { rows.push({ op: 'change', element: adds[a], before: dels[d], pieces: diffWords(dels[d].text, adds[a].text) }); d++; a++; }
            else if (d < dels.length) { rows.push({ op: 'del', element: dels[d] }); d++; }
            else { rows.push({ op: 'add', element: adds[a] }); a++; }
        }
    };
    let i = 0;
    let j = 0;
    for (const [bi, ai] of pairs) {
        flush(before.slice(i, bi), after.slice(j, ai));
        rows.push({ op: 'same', element: after[ai] });
        i = bi + 1;
        j = ai + 1;
    }
    flush(before.slice(i), after.slice(j));
    return rows;
};

/** A run of a changed line as shown: where it lies in the line's text, and whether it stayed, came in or went */
export interface ReviewSpan { from: number; to: number; op: WordPiece['op'] }

/**
 * A changed line as the review shows it: its words in reading order, what went struck through
 * where it was, what came in after it, a space between a struck word and the next; and where each
 * run lies in that text, so the line wraps on the page as one and every run is drawn in its place.
 */
export const reviewText = (row: DiffRow): { text: string; spans: ReviewSpan[] } => {
    if (row.op !== 'change' || !row.pieces) return { text: row.element.text, spans: [] };
    let text = '';
    const spans: ReviewSpan[] = [];
    for (const piece of row.pieces) {
        if (text && !/\s$/.test(text) && !/^\s/.test(piece.text)) text += ' ';
        const from = text.length;
        text += piece.text;
        spans.push({ from, to: text.length, op: piece.op });
    }
    return { text, spans };
};

/** The lines of a review as elements to lay on pages: a changed line with its struck words in it, the scenes counted anew */
export const reviewElements = (rows: ReadonlyArray<DiffRow>): Element[] => {
    let scene = -1;
    return rows.map(row => {
        if (row.element.kind === 'scene_heading') scene++;
        return { kind: row.element.kind, text: reviewText(row).text, scene };
    });
};

/** What changed, in a line: how many lines, in which scenes */
export const summarizeDiff = (rows: ReadonlyArray<DiffRow>): { changed: number; scenes: number[]; note: string } => {
    const touched = rows.filter(row => row.op !== 'same');
    const scenes = Array.from(new Set(touched.map(row => row.element.scene).filter(scene => scene >= 0))).sort((a, b) => a - b);
    const lines = `${touched.length} line${touched.length === 1 ? '' : 's'}`;
    const where = scenes.length === 0 ? '' : scenes.length === 1 ? `Scene ${scenes[0] + 1} changed` : `Scenes ${scenes.slice(0, -1).map(s => s + 1).join(', ')} and ${scenes[scenes.length - 1] + 1} changed`;
    return { changed: touched.length, scenes, note: touched.length === 0 ? 'Nothing changed.' : where ? `${where} · ${lines}` : `${lines} changed` };
};

/** Versions kept inside the card's record: so many, and so many characters together (the record holds a megabyte) */
export const MAX_VERSIONS = 40;
export const MAX_VERSION_CHARS = 600_000;

/** A version added, the oldest let go once there are too many or they are too long together */
export const addVersion = (versions: ReadonlyArray<ScreenplayVersion>, version: ScreenplayVersion): ScreenplayVersion[] => {
    const next = [...versions, version];
    while (next.length > MAX_VERSIONS || next.reduce((sum, v) => sum + v.text.length, 0) > MAX_VERSION_CHARS) {
        if (next.length <= 1) break;
        next.shift();
    }
    return next;
};

/** How many tokens an answer of so many pages needs: about a thousand a page, and room for the title page */
export const tokensForPages = (pages: number): number => Math.min(8, Math.max(1, pages)) * 1000 + 300;

/** The screenplay's text, as Fountain, for a file */
export const fountainFile = (text: string): Blob => new Blob([text.replace(/\r\n?/g, '\n')], { type: 'text/plain;charset=utf-8' });
